// src/components/DarkWebNewBusinessUnitModal.js
import React, { useState, useEffect } from "react";
import { X, Loader } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useModuleAvailability } from "../hooks/useModuleAvailability";

const DarkWebNewBusinessUnitModal = ({ isOpen, onClose, onBusinessUnitAdded, existingBUIds = [] }) => {
    const [businessUnits, setBusinessUnits] = useState([]);
    const [selectedBUId, setSelectedBUId] = useState("");
    const [domain, setDomain] = useState("");
    const [enableLiveMonitoring, setEnableLiveMonitoring] = useState(false);
    const [loadingBUs, setLoadingBUs] = useState(true);
    const [saving, setSaving] = useState(false);

    const apiCall = useApi();
    // Live monitoring (Enzoic) needs internet access; on Restricted/AirGapped installations the
    // checkbox is hidden and no enrollment call is made. The assessment itself is local.
    const { loaded: flagsLoaded, isModuleAvailable } = useModuleAvailability();
    const liveMonitoringAvailable = flagsLoaded && isModuleAvailable("darkWebMonitoring");

    // Fetch all business units when modal opens
    useEffect(() => {
        if (isOpen) fetchBusinessUnits();
    }, [isOpen]);

    const fetchBusinessUnits = async () => {
        try {
            const res = await apiCall("GET", `/businessUnits/all`);
            // Filter out BUs that are already added for this product category
            const availableBUs = (res || []).filter(bu => !existingBUIds.includes(bu.businessUnitId));
            setBusinessUnits(availableBUs);
        } catch (err) {} finally {
            setLoadingBUs(false);
        }
    };

    // Handle save (BU selection confirmation)
    const handleSave = async (e) => {
        e.preventDefault();

        if (!selectedBUId) {
            alert("Please select a business unit first.");
            return;
        }

        const selectedBU = businessUnits.find((bu) => bu.businessUnitId.toString() === selectedBUId);

        setSaving(true);
        try {
            // Update BusinessUnitCategory flag in backend
            await apiCall("PUT", `/businessUnits/businessUnitUpdate-category/${selectedBUId}`, { businessUnitCategory: "DarkWeb", domain: domain });

            // If live monitoring enabled and domain provided, initialize Enzoic
            if (liveMonitoringAvailable && enableLiveMonitoring && domain.trim()) {
                try {
                    await apiCall("POST", `/enzoic/initialize-business-unit/${selectedBUId}`, { domain: domain.trim() });
                } catch (monitoringError) {}
            }

            // Trigger parent callback and close modal
            onBusinessUnitAdded?.(selectedBU);
            onClose();
        } catch (error) {
            alert("Failed to add business unit. Check console for details.");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-6 border border-border w-full max-w-lg shadow-lg rounded-md bg-theme-secondary">

                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">Add Business Unit to Dark Web</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Unit</label>
                        <select
                            value={selectedBUId}
                            onChange={(e) => {
                                const id = e.target.value;
                                setSelectedBUId(id);
                                const bu = businessUnits.find((b) => b.businessUnitId.toString() === id);
                                setDomain(bu?.domain || "");
                            }}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            disabled={loadingBUs}
                        >
                            <option value="">-- Select a Business Unit --</option>
                            {businessUnits.map((bu) => (
                                <option
                                    key={bu.businessUnitId}
                                    value={bu.businessUnitId}
                                    title={bu.businessUnitName}
                                >
                                    {bu.businessUnitName}
                                </option>
                            ))}
                        </select>
                        {!loadingBUs && businessUnits.length === 0 && (
                            <p className="text-sm text-gray-500 mt-2">
                                No available business units. Create business units from the Business Units management page.
                            </p>
                        )}
                    </div>

                    {/* Domain field - shown when a BU is selected */}
                    {selectedBUId && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                            <input
                                type="text"
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                placeholder="e.g., example.com"
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                            <p className="mt-1 text-sm text-gray-500">
                                Please verify or update the domain name for this business unit, or leave blank if none. We will scan the Dark Web for any exposures related to the business unit.
                            </p>
                        </div>
                    )}

                    {/* Enable Live Monitoring checkbox - shown when live monitoring is available and a BU is selected */}
                    {selectedBUId && liveMonitoringAvailable && (
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="enableLiveMonitoringBU"
                                checked={enableLiveMonitoring}
                                onChange={(e) => setEnableLiveMonitoring(e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="enableLiveMonitoringBU" className="ml-2 text-sm text-gray-700">
                                Enable Live Monitoring
                            </label>
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!selectedBUId || saving}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                "Save"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DarkWebNewBusinessUnitModal;
